import { Box, Divider, Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { ReverseLeftIcon } from '@/components/icons/ReverseLeftIcon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';

import { useGrowthProfileModalContext } from '../GrowthProfileModal.context';
import {
  GrowthProfileModalFormPaths,
  GrowthProfileModalFormShape,
} from '../GrowthProfileModal.types';
import { TaxDragRateFormShape } from './TaxDrag.types';
import {
  getDefaultStateTaxRates,
  getReturnCompositionTotal,
  isValidReturnComposition,
} from './TaxDrag.utils';

interface ComputedTaxDragPanelProps {
  rate: TaxDragRateFormShape;
  ratePath: `taxDrag.globalRate` | `taxDrag.overrides.${number}`;
  stateCode?: string | null;
}

export function ComputedTaxDragPanel({
  rate,
  ratePath,
  stateCode,
}: ComputedTaxDragPanelProps) {
  const { disabled } = useFormFieldsDisabled();
  const { taxDragDefaults } = useGrowthProfileModalContext();
  const { control, getValues, setValue } =
    useFormContext<GrowthProfileModalFormShape>();
  const [
    federalIncomeTaxRate,
    federalLongTermCapitalGainsRate,
    stateIncomeTaxRate,
    stateLongTermCapitalGainsRate,
  ] = useWatch({
    control,
    name: [
      `${ratePath}.federalIncomeTaxRate`,
      `${ratePath}.federalLongTermCapitalGainsRate`,
      `${ratePath}.stateIncomeTaxRate`,
      `${ratePath}.stateLongTermCapitalGainsRate`,
    ],
    exact: true,
  });
  const currentRates = {
    federalIncomeTaxRate,
    federalLongTermCapitalGainsRate,
    stateIncomeTaxRate,
    stateLongTermCapitalGainsRate,
  };
  const defaultStateRates = getDefaultStateTaxRates(stateCode, taxDragDefaults);
  const hasStateDefaults =
    defaultStateRates.income != null && defaultStateRates.capitalGains != null;
  const defaultRates = {
    federalIncomeTaxRate: taxDragDefaults?.federalIncomeTaxRate,
    federalLongTermCapitalGainsRate:
      taxDragDefaults?.federalLongTermCapitalGainsRate,
    stateIncomeTaxRate: defaultStateRates.income,
    stateLongTermCapitalGainsRate: defaultStateRates.capitalGains,
  };
  const hasCustomRates = Object.entries(defaultRates).some(
    ([name, value]) =>
      value != null &&
      !currentRates[name as keyof typeof defaultRates]?.equals(value)
  );
  const total = getReturnCompositionTotal(rate);

  const field = (suffix: keyof TaxDragRateFormShape) =>
    `${ratePath}.${suffix}` as GrowthProfileModalFormPaths;

  const validateComposition = () => {
    const currentRate = getValues(ratePath) as TaxDragRateFormShape;
    return (
      isValidReturnComposition(currentRate) ||
      'Return composition must total 100%.'
    );
  };
  const compositionValidation = {
    compositionTotal: validateComposition,
  };

  const revertRates = () => {
    for (const [name, value] of Object.entries(defaultRates)) {
      if (value == null) continue;
      setValue(field(name as keyof typeof defaultRates), value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Box
      data-testid="computed-tax-drag-panel"
      sx={{ bgcolor: COLORS.GRAY[50], borderRadius: 1, p: 2 }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="title-s">Computed tax drag</Typography>
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            When computed tax drag is used, tax drag is computed from the inputs
            below instead of entered directly in the field above. To return to
            manual entry, select &apos;Clear computed tax drag&apos;.
          </Typography>
          {!hasStateDefaults && (
            <Typography variant="body-xs">
              No state defaults are available for this residence. Enter the
              applicable state rates.
            </Typography>
          )}
        </Stack>

        <FormAwareSwitch<GrowthProfileModalFormShape>
          control={control}
          fieldName={field('niitEnabled')}
          label="Apply 3.8% NIIT"
          labelPosition="right"
        />

        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Typography variant="title-s">Tax rate</Typography>
            <ContextualHelpTooltip>
              <PopperContent body="Federal and state income tax rates apply to ordinary income. Capital gains rates apply to qualified dividends and long-term capital gains. NIIT is added when selected." />
            </ContextualHelpTooltip>
          </Stack>
          <Button
            aria-label="Revert to defaults"
            disabled={disabled || !hasCustomRates}
            variant="transparent"
            size="xs"
            startIcon={ReverseLeftIcon}
            onClick={revertRates}
          >
            Revert to defaults
          </Button>
        </Stack>
        <FormLayoutRow>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('federalIncomeTaxRate')}
              label="Federal income"
              isDecimalJSInput
              required
            />
          </FormLayoutItem>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('federalLongTermCapitalGainsRate')}
              label="Federal cap gains"
              isDecimalJSInput
              required
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('stateIncomeTaxRate')}
              label="State income"
              isDecimalJSInput
              required
            />
          </FormLayoutItem>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('stateLongTermCapitalGainsRate')}
              label="State cap gains"
              isDecimalJSInput
              required
            />
          </FormLayoutItem>
        </FormLayoutRow>

        <Divider />
        <Stack direction="row" alignItems="center">
          <Typography variant="title-s">Return composition</Typography>
          <ContextualHelpTooltip>
            <PopperContent body="The share of annual investment return in each income category. The four shares must total 100%. Tax-free income does not contribute to computed tax drag." />
          </ContextualHelpTooltip>
        </Stack>
        <FormLayoutRow>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('ordinaryIncomeReturnPercentage')}
              label="Ordinary income"
              isDecimalJSInput
              required
              validation={compositionValidation}
            />
          </FormLayoutItem>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('taxFreeIncomeReturnPercentage')}
              label="Tax-free income (Federal and state)"
              isDecimalJSInput
              required
              validation={compositionValidation}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('qualifiedDividendReturnPercentage')}
              label="Qualified dividend"
              isDecimalJSInput
              required
              validation={compositionValidation}
            />
          </FormLayoutItem>
          <FormLayoutItem width={6}>
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={field('longTermCapitalGainReturnPercentage')}
              label="Capital gain (long term)"
              isDecimalJSInput
              required
              validation={compositionValidation}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="title-s">Total</Typography>
          <Typography variant="title-s">
            {total == null ? '—' : `${total.toString()}%`}
          </Typography>
        </Stack>
        {!isValidReturnComposition(rate) && (
          <Typography
            variant="body-xs"
            sx={{ color: COLORS.FUNCTIONAL.ERROR[700] }}
          >
            Return composition must total 100%.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
